import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Newspaper, Calendar, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { getBlogPosts } from '@/lib/api';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const res = await getBlogPosts();
        setPosts(res.data);
      } catch (error) {
        console.error('Error loading blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPosts();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative py-20 md:py-32 bg-terracotta-light/20 overflow-hidden">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Newspaper className="w-12 h-12 text-terracotta mx-auto mb-6" />
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-charcoal mb-6" data-testid="blog-title">
              Dernières nouvelles
            </h1>
            <p className="text-stone text-lg max-w-xl mx-auto">
              Suivez les dernières actualités et préparatifs de notre mariage.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-pulse text-stone">Chargement...</div>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <Newspaper className="w-16 h-16 text-stone/30 mx-auto mb-4" />
              <p className="text-stone text-lg">Aucune actualité pour le moment.</p>
              <p className="text-stone/70 mt-2">Revenez bientôt pour découvrir nos dernières nouvelles !</p>
            </div>
          ) : (
            <div className="space-y-8">
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Card className="card-elegant overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="flex flex-col md:flex-row">
                      {post.image_url && (
                        <div className="md:w-1/3 h-48 md:h-auto">
                          <img
                            src={post.image_url}
                            alt={post.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <CardContent className={`flex-1 p-6 ${post.image_url ? '' : 'w-full'}`}>
                        <div className="flex items-center gap-2 text-sm text-stone mb-3">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(post.created_at)}</span>
                        </div>
                        <h2 className="font-display text-2xl text-charcoal mb-3">
                          {post.title}
                        </h2>
                        <p className="text-stone leading-relaxed mb-4">
                          {post.excerpt || post.content.substring(0, 200) + (post.content.length > 200 ? '...' : '')}
                        </p>
                        <Link 
                          to={`/actualites/${post.id}`}
                          className="inline-flex items-center gap-2 text-terracotta font-medium hover:text-terracotta-dark transition-colors"
                        >
                          Lire la suite
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </CardContent>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
};

export default BlogPage;
